import posthog from '../services/posthog';

export const ANALYTICS_EVENTS = {
  PAGE_VIEW: 'page_view',

  MOBILE_WELCOME_SEEN: 'mobile_welcome_seen',
  MOBILE_WELCOME_CREATE_ACCOUNT_CLICKED: 'mobile_welcome_create_account_clicked',
  MOBILE_WELCOME_LOGIN_CLICKED: 'mobile_welcome_login_clicked',
  MOBILE_WELCOME_EXPLORE_FIRST_CLICKED: 'mobile_welcome_explore_first_clicked',

  AUTH_MODE_TOGGLED: 'auth_mode_toggled',
  SIGNUP_SUBMITTED: 'signup_submitted',
  SIGNUP_SUCCEEDED: 'signup_succeeded',
  SIGNUP_FAILED: 'signup_failed',
  LOGIN_SUBMITTED: 'login_submitted',
  LOGIN_SUCCEEDED: 'login_succeeded',
  LOGIN_FAILED: 'login_failed',
  CAPTCHA_MISSING: 'captcha_missing',
  CAPTCHA_VERIFICATION_FAILED: 'captcha_verification_failed',
  PASSWORD_RESET_SUBMITTED: 'password_reset_submitted',
  PASSWORD_RESET_SUCCEEDED: 'password_reset_succeeded',
  PASSWORD_RESET_FAILED: 'password_reset_failed',
  PASSWORD_UPDATE_SUBMITTED: 'password_update_submitted',
  PASSWORD_UPDATE_SUCCEEDED: 'password_update_succeeded',
  PASSWORD_UPDATE_FAILED: 'password_update_failed',
  ZIP_VALIDATION_FAILED: 'zip_validation_failed',
  RESEND_VERIFICATION_CLICKED: 'resend_verification_clicked',
  RESEND_VERIFICATION_SUCCEEDED: 'resend_verification_succeeded',
  RESEND_VERIFICATION_FAILED: 'resend_verification_failed',

  FEED_LOADED: 'feed_loaded',
  FEED_LOAD_MORE: 'feed_load_more',
  SORT_CHANGED: 'sort_changed',
  FILTERS_CLEARED: 'filters_cleared',
  SOURCE_FILTER_CHANGED: 'source_filter_changed',
  ZERO_RESULT_FILTERED_STATE: 'zero_result_filtered_state',

  CAMPAIGN_CARD_CLICKED: 'campaign_card_clicked',
  CAMPAIGN_DETAIL_VIEWED: 'campaign_detail_viewed',
  CAMPAIGN_SOURCE_CLICKED: 'campaign_source_clicked',
  CAMPAIGN_SHARED: 'campaign_shared',

  CAMPAIGN_SUPPORT_CLICKED: 'campaign_support_clicked',
  CAMPAIGN_OPPOSE_CLICKED: 'campaign_oppose_clicked',
  CAMPAIGN_VOTE_CLICKED: 'campaign_vote_clicked',

  SIGNIN_PROMPT_SHOWN: 'signin_prompt_shown',
  SAVED_ORG_CONFIRM_SHOWN: 'saved_org_confirm_shown',
  ORG_SELECT_MODAL_SHOWN: 'org_select_modal_shown',

  ORG_SELECT_OPENED: 'org_select_opened',
  ORG_SELECT_LEVEL_CHANGED: 'org_select_level_changed',
  ORG_SELECT_STATE_CHANGED: 'org_select_state_changed',
  ORG_SELECTED: 'org_selected',
  ORG_SELECT_EMPTY_STATE_SEEN: 'org_select_empty_state_seen',

  CONTRIBUTION_DETAILS_VIEWED: 'contribution_details_viewed',
  CONTRIBUTION_ORG_PRESENT: 'contribution_org_present',
  CONTRIBUTION_AMOUNT_ENTERED: 'contribution_amount_entered',
  CONTRIBUTION_COMMENT_ENTERED: 'contribution_comment_entered',
  REDIRECT_SUBMIT_ATTEMPTED: 'redirect_submit_attempted',
  REDIRECT_EVENT_CREATED: 'redirect_event_created',
  REDIRECT_EVENT_FAILED: 'redirect_event_failed',
  REDIRECT_OPENED: 'redirect_opened',
  RETURN_TO_BALLOTR_DETECTED: 'return_to_ballotr_detected',
  SELF_REPORTED_COMPLETION_CLICKED: 'self_reported_completion_clicked',
  SELF_REPORTED_COMPLETION_SUCCEEDED: 'self_reported_completion_succeeded',
  SELF_REPORTED_COMPLETION_FAILED: 'self_reported_completion_failed',
  REDIRECT_CANCELED: 'redirect_canceled',

  PROFILE_VIEWED: 'profile_viewed',
  PROFILE_PARTY_UPDATED: 'profile_party_updated',
  PASSWORD_CHANGE_ATTEMPTED: 'password_change_attempted',
  PASSWORD_CHANGE_SUCCEEDED: 'password_change_succeeded',
  PASSWORD_CHANGE_FAILED: 'password_change_failed',
  CONTRIBUTION_TAB_FILTER_USED: 'contribution_tab_filter_used',
  ORG_FILTER_USED: 'org_filter_used',
  PROFILE_SORT_CHANGED: 'profile_sort_changed',
  PROFILE_PAGINATION_USED: 'profile_pagination_used',

  REPORT_PROBLEM_OPENED: 'report_problem_opened',
  REPORT_PROBLEM_SUBMITTED: 'report_problem_submitted',
  REPORT_PROBLEM_FAILED: 'report_problem_failed',
  CONTACT_SUPPORT_OPENED: 'contact_support_opened',
  CONTACT_SUPPORT_SUBMITTED: 'contact_support_submitted',
  CONTACT_SUPPORT_FAILED: 'contact_support_failed',
} as const;

type AnalyticsEventName =
  (typeof ANALYTICS_EVENTS)[keyof typeof ANALYTICS_EVENTS];

type AnalyticsProperties = Record<string, unknown>;

type AuthFailureFlow =
  | 'signup'
  | 'login'
  | 'password_reset'
  | 'password_update'
  | 'password_change';

type AuthFailureInput = {
  flow: AuthFailureFlow;
  location: string;
  error: unknown;
  extraProperties?: AnalyticsProperties;
};

const isAnalyticsEnabled = (): boolean => {
  return Boolean(process.env.REACT_APP_POSTHOG_KEY);
};

const normalizeErrorMessage = (error: unknown): string => {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  if (error && typeof error === 'object' && 'message' in error) {
    const message = (error as { message?: unknown }).message;
    return typeof message === 'string' ? message : 'Unknown error';
  }
  return 'Unknown error';
};

const normalizeErrorCode = (error: unknown): string | number | null => {
  if (!error || typeof error !== 'object') return null;

  const candidate = error as {
    code?: unknown;
    status?: unknown;
    statusCode?: unknown;
  };

  if (typeof candidate.code === 'string' || typeof candidate.code === 'number') {
    return candidate.code;
  }

  if (typeof candidate.status === 'string' || typeof candidate.status === 'number') {
    return candidate.status;
  }

  if (
    typeof candidate.statusCode === 'string' ||
    typeof candidate.statusCode === 'number'
  ) {
    return candidate.statusCode;
  }

  return null;
};

const getAuthFailureEventName = (flow: AuthFailureFlow): AnalyticsEventName => {
  switch (flow) {
    case 'signup':
      return ANALYTICS_EVENTS.SIGNUP_FAILED;
    case 'login':
      return ANALYTICS_EVENTS.LOGIN_FAILED;
    case 'password_reset':
      return ANALYTICS_EVENTS.PASSWORD_RESET_FAILED;
    case 'password_update':
      return ANALYTICS_EVENTS.PASSWORD_UPDATE_FAILED;
    case 'password_change':
      return ANALYTICS_EVENTS.PASSWORD_CHANGE_FAILED;
    default:
      return ANALYTICS_EVENTS.LOGIN_FAILED;
  }
};

export const trackEvent = (
  eventName: AnalyticsEventName,
  properties: AnalyticsProperties = {}
): void => {
  if (!isAnalyticsEnabled()) return;
  posthog.capture(eventName, properties);
};

export const trackPageView = (
  pageName: string,
  properties: AnalyticsProperties = {}
): void => {
  trackEvent(ANALYTICS_EVENTS.PAGE_VIEW, {
    page_name: pageName,
    ...properties,
  });
};

export const trackAuthFailure = ({
  flow,
  location,
  error,
  extraProperties = {},
}: AuthFailureInput): void => {
  const errorMessage = normalizeErrorMessage(error);
  const errorCode = normalizeErrorCode(error);
  const normalizedMessage = errorMessage.toLowerCase();

  trackEvent(getAuthFailureEventName(flow), {
    flow,
    location,
    error_message: errorMessage,
    error_code: errorCode,
    ...extraProperties,
  });

  if (normalizedMessage.includes('captcha')) {
    trackEvent(ANALYTICS_EVENTS.CAPTCHA_VERIFICATION_FAILED, {
      flow,
      location,
      error_message: errorMessage,
      error_code: errorCode,
      ...extraProperties,
    });
  }
};